<script setup lang="ts">
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    options: { label: string; value: string; disabled?: boolean }[];
    disabled?: boolean;
    style?: 'primary' | 'secondary';
    size?: 'sm' | 'md';
  }>(),
  {
    style: 'primary',
    size: 'md',
  }
);
const emits = defineEmits<{ (e: 'clickOption', value: string) }>();

const domId = useId();
const containerRef = ref<HTMLElement | null>(null);
const displayDropdown = ref(false);
const dropdownTop = ref(0);
const dropdownWidth = ref(0);

const buttonId = computed(() => `dropdown-button-${domId}`);

const customClass = computed(() => `dropdown-button__button--${props.style} dropdown-button__button--${props.size}`);

/**
 * ドロップダウン表示切り替え
 */
const toggleDropdown = () => {
  displayDropdown.value = !displayDropdown.value;
};

/**
 * ドロップダウンを計算
 */
const calcDropdown = async () => {
  await nextTick();
  const button = document.getElementById(buttonId.value);
  if (!button) {
    dropdownWidth.value = 0;
    return;
  }
  dropdownTop.value = button.offsetTop + button.clientHeight;
  if (props.style === 'secondary') dropdownTop.value += 2;
  dropdownWidth.value = button.clientWidth;
};

/**
 * ドロップダウンを閉じる
 * @param event - クリックイベント
 */
const closeDropdown = (event: Event) => {
  if (!displayDropdown.value) return;
  const container = containerRef.value;
  if (container && event.composedPath().includes(container)) return;

  displayDropdown.value = false;
};

/**
 * 選択肢をクリックした際の処理
 * @param value - クリックした選択肢の値
 */
const onClickOptions = (value: string) => {
  displayDropdown.value = false;
  emits('clickOption', value);
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown);
});

watch(
  () => displayDropdown.value,
  async (v) => {
    if (v) await calcDropdown();
  },
  { immediate: true }
);

watch(
  () => i18n.locale,
  async () => {
    await calcDropdown();
  }
);
</script>
<template>
  <div ref="containerRef" class="dropdown-button">
    <button :id="buttonId" class="dropdown-button__button" :class="customClass" :disabled="props.disabled" @click="toggleDropdown">
      <slot name="label" />
      <Mdi v-if="displayDropdown" icon="expand_less" />
      <Mdi v-else icon="expand_more" />
    </button>
    <div v-if="displayDropdown" class="dropdown-button__dropdown">
      <button
        v-for="option in props.options"
        :key="option.value"
        :disabled="option.disabled"
        class="dropdown-button__option"
        @click="onClickOptions(option.value)"
      >
        {{ t(option.label) }}
      </button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.dropdown-button {
  *:not(.material-icons) {
    font-family: 'Noto Sans JP';
  }
  &__button {
    &--primary {
      background-color: $semanticPrimary;
      color: $semanticWhite;
    }
    &--secondary {
      background-color: $semanticWhite;
      color: $semanticPrimary;
      border: 1px solid $borderActive;
    }
    &--md {
      padding: 8px 16px;
    }
    &--sm {
      padding: 2px 16px;
    }
    border-radius: 4px;
    display: grid;
    grid-template-columns: max-content max-content;
    align-items: center;
    font-weight: 700;
    font-size: 14px;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
    &:disabled {
      cursor: not-allowed;
      box-shadow: none;
      opacity: 0.6;
    }
  }

  &__dropdown {
    position: fixed;
    width: v-bind('`${dropdownWidth}px`');
    top: v-bind('`${dropdownTop}px`');
    display: grid;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 50;
    overflow-y: auto;
    box-sizing: border-box;
  }
  &__option {
    width: 100%;
    padding: 10px;
    text-align: start;
    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
